/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';

import Link from '@salesforce/retail-react-app/app/components/link';
import {Box, Heading, List, ListItem, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

const LinksList = ({
  links = [],
  heading = '',
  variant,
  color,
  onLinkClick,
  headingLinkRef,
  ...otherProps
}) => {
  const styles = useMultiStyleConfig('LinksList', {variant});

  return (
    <Box {...styles.container} {...(color ? {color: color} : {})} {...otherProps}>
      {heading &&
        (heading.href ? (
          <Link
            ref={headingLinkRef}
            to={heading.href}
            onClick={onLinkClick}
            {...styles.headingLink}
          >
            <Heading {...styles.heading} {...(heading.styles ? heading.styles : {})}>
              {heading.text}
            </Heading>
          </Link>
        ) : (
          <Heading {...styles.heading}>{heading}</Heading>
        ))}

      {links &&
        (variant === 'horizontal' ? (
          <List {...styles.list} data-testid="horizontal-list">
            {links.map((link, i) => (
              <ListItem key={i} {...styles.listItem} sx={styles.listItemSx}>
                <Link
                  to={link.href}
                  onClick={onLinkClick}
                  {...(link.styles ? link.styles : {})}
                >
                  {link.text}
                </Link>
              </ListItem>
            ))}
          </List>
        ) : (
          <List spacing={5} {...styles.list}>
            {links.map((link, i) => (
              <ListItem key={i}>
                <Link
                  to={link.href}
                  onClick={onLinkClick}
                  {...(link.styles ? link.styles : {})}
                >
                  {link.text}
                </Link>
              </ListItem>
            ))}
          </List>
        ))}
    </Box>
  );
};

LinksList.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  variant: PropTypes.oneOf(['vertical', 'horizontal']),
  color: PropTypes.string,
  onLinkClick: PropTypes.func,
  headingLinkRef: PropTypes.object,
};

export default LinksList;
